import { useState, useEffect } from 'react';
import {
  Drawer,
  Table,
  Button,
  message,
  Spin,
  Empty,
  Tag,
  Checkbox,
  Grid,
  InputNumber,
  Tooltip,
  Input,
  Space,
} from 'antd';
import { DeleteOutlined, LoadingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { useGetSingleRFID, useUpdateRFID } from '../../Pages/rfid-manager/Hook';
import { useOperatorStore } from '../../store/useOperatorStore';
import { useAuthStore } from '../../store/useAuthStore';
import { useQueryClient } from '@tanstack/react-query';
import {
  DebouncedSelect,
  fetchLocationList,
  fetchChargerList,
  fetchCustomerList,
} from '../useDebouncedSelectLocation';
import { formatUnixToLocalDate, formatKwFromWatts } from '../../utils/utils';

const { useBreakpoint } = Grid;
const { TextArea } = Input;

const FieldLabel = ({ children, required }) => (
  <div className="mb-1.5 text-[13px] font-medium text-slate-600">
    {children}
    {required ? <span className="text-red-500 ml-0.5">*</span> : null}
  </div>
);

FieldLabel.propTypes = {
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
};

const SectionCard = ({ title, children, className = '' }) => (
  <section
    className={`rounded-xl border border-slate-200 bg-white p-4 sm:p-5 ${className}`}
  >
    {title ? (
      <h3 className="mb-4 text-sm font-semibold tracking-wide text-slate-800 uppercase">
        {title}
      </h3>
    ) : null}
    {children}
  </section>
);

SectionCard.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

const StatBlock = ({ label, value, tone = 'default', tooltip }) => {
  const toneClass = {
    default: 'text-slate-800',
    success: 'text-emerald-600',
    warning: 'text-amber-600',
    danger: 'text-red-600',
    info: 'text-sky-700',
    muted: 'text-slate-400',
  }[tone];

  const content = (
    <div className="rounded-lg bg-slate-50 px-3.5 py-3 border border-slate-100 h-full">
      <div className="text-[12px] font-medium text-slate-500 mb-1">{label}</div>
      <div className={`text-base font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );

  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
};

StatBlock.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.oneOf(['default', 'success', 'warning', 'danger', 'info', 'muted']),
  tooltip: PropTypes.string,
};

const ViewRFIDDrawer = ({ chargerData, setOpen, open, refetchRFIDList }) => {
  const { t } = useTranslation('ViewRFID');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { operatorId } = useOperatorStore();
  const { user } = useAuthStore();
  const isReadOnlyUser = user?.role === 'READ_ONLY_ADMIN';
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useGetSingleRFID({
    id: chargerData?._id,
    operatorId,
  });

  const { mutate: updateRFID, isPending: isUpdating } = useUpdateRFID(
    chargerData?._id
  );

  const [localLocations, setLocalLocations] = useState([]);
  const [localChargers, setLocalChargers] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [isGlobal, setIsGlobal] = useState(chargerData?.isGlobal || false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [limitAmount, setLimitAmount] = useState(0);
  const [rfidTag, setRfidTag] = useState(chargerData?.RFID || '');
  const [description, setDescription] = useState(chargerData?.description || '');

  useEffect(() => {
    if (data) {
      const locs = (data.locations || []).map((loc) => ({
        _id: loc._id,
        locationName: loc.locationName ?? loc.name ?? '-',
        address: loc.address ?? { city: '', state: '' },
      }));
      setLocalLocations(locs);
      const chs = (data.chargers || []).map((ch) => ({
        _id: ch._id,
        name: ch.name ?? '-',
        serialNumber: ch.serialNumber ?? '',
        status: ch.status ?? '',
      }));
      setLocalChargers(chs);
      setIsGlobal(data.isGlobal ?? false);
      setRfidTag(data.RFID ?? '');
      setDescription(data.description ?? '');
      if (!data.prePaid) {
        setLimitAmount(data.limit != null ? Number(data.limit) / 1000 : 0);
      }
      if (data.customer?._id) {
        const cust = data.customer;
        const label =
          `${cust?.fname || ''} ${cust?.lname || ''}`.trim() +
          (cust?.phoneNumber ? ` - ${cust.phoneNumber}` : '');
        setSelectedCustomer({
          value: cust._id,
          label: label || cust._id,
        });
      } else {
        setSelectedCustomer(null);
      }
    }
  }, [data]);

  const handleRemoveLocation = (locationId) => {
    if (isReadOnlyUser) return;
    setLocalLocations((prev) => prev.filter((loc) => loc._id !== locationId));
  };

  const handleRemoveCharger = (chargerId) => {
    if (isReadOnlyUser) return;
    setLocalChargers((prev) =>
      prev.filter((charger) => charger._id !== chargerId)
    );
  };

  const handleAddLocation = (_, option) => {
    if (isReadOnlyUser) return;
    if (!localLocations.some((loc) => loc._id === option.value)) {
      const locationData = option.data || {};

      setLocalLocations([
        ...localLocations,
        {
          _id: option.value,
          locationName: locationData.locationName || option.label.split(' - ')[0],
          address: locationData.address || {
            city: '',
            state: '',
          },
        },
      ]);
      setSelectedLocation(null);
    }
  };

  const handleAddCharger = (_, option) => {
    if (isReadOnlyUser) return;
    if (!localChargers.some((charger) => charger._id === option.value)) {
      const chargerOptionData = option.data || {};

      setLocalChargers([
        ...localChargers,
        {
          _id: option.value,
          name: chargerOptionData.name || option.label.split(' - ')[0],
          serialNumber: chargerOptionData.serialNumber || '',
          status: chargerOptionData.status || '',
        },
      ]);
      setSelectedCharger(null);
    }
  };

  const handleUpdateRFID = () => {
    if (isReadOnlyUser) return;

    const trimmedTag = (rfidTag || '').trim();
    if (!trimmedTag) {
      message.error(t('ViewRFIDDrawer.RFIDRequired'));
      return;
    }
    if (trimmedTag.length > 20) {
      message.error(t('ViewRFIDDrawer.RFIDMax'));
      return;
    }

    const customerIdValue =
      selectedCustomer?.value ?? selectedCustomer ?? null;
    const isPrePaid = data?.prePaid ?? chargerData?.prePaid;
    const payload = {
      operatorId,
      RFID: trimmedTag,
      customerId: customerIdValue ? String(customerIdValue) : '',
      locationId: isGlobal ? [] : localLocations.map((loc) => loc._id),
      chargersId: isGlobal ? [] : localChargers.map((charger) => charger._id),
      description: (description || '').trim(),
      isGlobal,
      ...(!isPrePaid
        ? { limit: (Number(limitAmount) || 0) * 1000 }
        : {}),
    };
    updateRFID(payload, {
      onSuccess: () => {
        message.success(t('ViewRFIDDrawer.UpdateSuccess'));
        refetch();
        refetchRFIDList?.();
        queryClient.invalidateQueries({ queryKey: ['customer-details'] });
        queryClient.invalidateQueries({ queryKey: ['fetch-RFID'] });
        setOpen(false);
      },
      onError: (error) => {
        message.error(error?.response?.data?.message || t('ViewRFIDDrawer.UpdateError'));
      },
    });
  };

  const rfidData = data ?? chargerData;
  const isPrePaid = rfidData?.prePaid;
  const status = rfidData?.status ?? '-';
  const isAccepted = status === 'Accepted';

  const getMonthlyUsageMeta = () => {
    const monthly = Number(rfidData?.monthlyUsageSoFar ?? 0);
    const limit = Number(rfidData?.limit ?? 0);
    const overLimit = !isPrePaid && limit > 0 && monthly >= limit;
    const nearLimit = !isPrePaid && limit > 0 && monthly >= limit * 0.75 && !overLimit;

    let tone = 'muted';
    if (!isPrePaid && limit > 0) {
      const ratio = monthly / limit;
      if (ratio >= 1) tone = 'danger';
      else if (ratio >= 0.75) tone = 'warning';
      else if (monthly > 0) tone = 'success';
    } else if (monthly > 0) {
      tone = 'success';
    }

    const tooltip = overLimit
      ? t('RFIDTable.MonthlyLimitReached', { ns: 'rfid' })
      : nearLimit
        ? t('RFIDTable.MonthlyLimitApproaching', { ns: 'rfid' })
        : undefined;

    const value =
      !isPrePaid && limit > 0
        ? t('ViewRFIDDrawer.MonthlyUsageOfLimit', {
            used: formatKwFromWatts(monthly),
            limit: formatKwFromWatts(limit),
          })
        : `${formatKwFromWatts(monthly)} kW`;

    return { tone, tooltip, value };
  };

  const monthlyMeta = getMonthlyUsageMeta();
  const lifetime = Number(rfidData?.lifetimeUsageSoFar ?? 0);

  const renderRFIDDetails = () => (
    <div className="space-y-4">
      <SectionCard>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-5 pb-4 border-b border-slate-100">
          <Space size={8} wrap>
            <Tag color={isAccepted ? 'success' : 'error'} className="m-0! px-2.5 py-0.5">
              {status}
            </Tag>
            <Tag color={isPrePaid ? 'green' : 'blue'} className="m-0! px-2.5 py-0.5">
              {isPrePaid ? t('ViewRFIDDrawer.PrePaid') : t('ViewRFIDDrawer.PostPaid')}
            </Tag>
          </Space>
          <span className="text-xs text-slate-500">
            {t('ViewRFIDDrawer.CreatedAt')}:{' '}
            <span className="text-slate-700">
              {rfidData?.created_at
                ? formatUnixToLocalDate(rfidData.created_at)
                : '—'}
            </span>
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <FieldLabel required>{t('ViewRFIDDrawer.RFIDLabel')}</FieldLabel>
            <Input
              value={rfidTag}
              maxLength={20}
              disabled={isReadOnlyUser}
              onChange={(e) => setRfidTag(e.target.value)}
              placeholder={t('ViewRFIDDrawer.RFIDPlaceholder')}
              className="font-mono"
            />
          </div>

          <div className="sm:col-span-2">
            <FieldLabel>{t('ViewRFIDDrawer.AssignedTo')}</FieldLabel>
            <DebouncedSelect
              placeholder={t('ViewRFIDDrawer.SearchCustomerPlaceholder')}
              fetchOptions={(search) =>
                fetchCustomerList({ operatorId, search })
              }
              allowClear
              labelInValue
              disabled={isReadOnlyUser}
              value={selectedCustomer}
              onChange={setSelectedCustomer}
              style={{ width: '100%' }}
            />
            <p className="mt-1.5 mb-0 text-xs text-slate-500">
              {t('ViewRFIDDrawer.AssignHelpText')}
            </p>
          </div>

          {isPrePaid ? (
            <div className="sm:col-span-2">
              <FieldLabel>{t('ViewRFIDDrawer.Balance')}</FieldLabel>
              <div className="h-8 flex items-center text-sm font-semibold tabular-nums text-slate-800">
                {formatKwFromWatts(rfidData?.balance ?? 0)} kW
              </div>
            </div>
          ) : (
            <div className="sm:col-span-2">
              <FieldLabel>{t('ViewRFIDDrawer.LimitLabel')}</FieldLabel>
              <InputNumber
                min={0}
                step={0.01}
                value={limitAmount}
                disabled={isReadOnlyUser}
                onChange={setLimitAmount}
                addonAfter="kW"
                className="w-full! max-w-xs"
              />
              <p className="mt-1.5 mb-0 text-xs text-slate-500">
                {t('ViewRFIDDrawer.LimitHelpText')}
              </p>
            </div>
          )}

          <div className="sm:col-span-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-3">
              <div className="flex items-center justify-between gap-3">
                <Checkbox
                  checked={isGlobal}
                  disabled={isReadOnlyUser}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                >
                  <span className="text-[13px] font-medium text-slate-700">
                    {t('ViewRFIDDrawer.GlobalCheckbox')}
                  </span>
                </Checkbox>
                <Tooltip title={t('ViewRFIDDrawer.GlobalTooltip')}>
                  <InfoCircleOutlined className="text-slate-400 shrink-0" />
                </Tooltip>
              </div>
              <p className="mt-1.5 mb-0 ml-6 text-xs text-slate-500 leading-relaxed">
                {t('ViewRFIDDrawer.GlobalTooltip')}
              </p>
            </div>
          </div>

          <div className="sm:col-span-2">
            <FieldLabel>{t('ViewRFIDDrawer.Description')}</FieldLabel>
            <TextArea
              value={description}
              disabled={isReadOnlyUser}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder={t('ViewRFIDDrawer.DescriptionPlaceholder')}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t('ViewRFIDDrawer.UsageTitle')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <StatBlock
            label={t('ViewRFIDDrawer.MonthlyUsage')}
            value={monthlyMeta.value}
            tone={monthlyMeta.tone}
            tooltip={monthlyMeta.tooltip}
          />
          <StatBlock
            label={t('ViewRFIDDrawer.LifetimeUsage')}
            value={`${formatKwFromWatts(lifetime)} kW`}
            tone={lifetime > 0 ? 'info' : 'muted'}
          />
        </div>
      </SectionCard>
    </div>
  );

  const renderLocationsSection = () => (
    <SectionCard title={t('ViewRFIDDrawer.LocationsTitle')} className="mt-4">
      <DebouncedSelect
        placeholder={t('ViewRFIDDrawer.AddLocationPlaceholder')}
        className="w-full sm:w-72"
        disabled={isReadOnlyUser}
        fetchOptions={(search) =>
          fetchLocationList({ operatorId, search })
        }
        value={selectedLocation}
        onChange={handleAddLocation}
      />
      {localLocations.length > 0 ? (
        <Table
          columns={[
            {
              title: t('ViewRFIDDrawer.Table.LocationName'),
              dataIndex: 'locationName',
              key: 'locationName',
            },
            {
              title: t('ViewRFIDDrawer.Table.City'),
              dataIndex: ['address', 'city'],
              key: 'city',
              render: (_, record) => record.address?.city || '-',
            },
            {
              title: t('ViewRFIDDrawer.Table.State'),
              dataIndex: ['address', 'state'],
              key: 'state',
              render: (_, record) => record.address?.state || '-',
            },
            {
              title: t('ViewRFIDDrawer.Table.Action'),
              key: 'action',
              width: 72,
              align: 'center',
              render: (_, record) => (
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  disabled={isReadOnlyUser}
                  onClick={() => handleRemoveLocation(record._id)}
                />
              ),
            },
          ]}
          dataSource={localLocations}
          pagination={false}
          rowKey="_id"
          size="small"
          className="mt-4"
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-6">
          <Empty description={t('ViewRFIDDrawer.NoLocations')} />
        </div>
      )}
    </SectionCard>
  );

  const renderChargersSection = () => (
    <SectionCard title={t('ViewRFIDDrawer.ChargersTitle')} className="mt-4">
      <DebouncedSelect
        className="w-full sm:w-72"
        placeholder={t('ViewRFIDDrawer.AddChargerPlaceholder')}
        disabled={isReadOnlyUser}
        fetchOptions={(search) =>
          fetchChargerList({ operatorId, search })
        }
        value={selectedCharger}
        onChange={handleAddCharger}
      />
      {localChargers.length > 0 ? (
        <Table
          columns={[
            {
              title: t('ViewRFIDDrawer.Table.SerialNumber') || 'Serial Number',
              dataIndex: 'serialNumber',
              key: 'serialNumber',
            },
            {
              title: t('ViewRFIDDrawer.Table.Name'),
              dataIndex: 'name',
              key: 'name',
            },
            {
              title: t('ViewRFIDDrawer.Table.Status') || 'Status',
              dataIndex: 'status',
              key: 'status',
              render: (chargerStatus) => (
                <Tag color={chargerStatus === 'ONLINE' ? 'green' : 'red'} variant="outlined">
                  {chargerStatus}
                </Tag>
              ),
            },
            {
              title: t('ViewRFIDDrawer.Table.Action'),
              key: 'action',
              width: 72,
              align: 'center',
              render: (_, record) => (
                <Button
                  danger
                  type="text"
                  icon={<DeleteOutlined />}
                  disabled={isReadOnlyUser}
                  onClick={() => handleRemoveCharger(record._id)}
                />
              ),
            },
          ]}
          dataSource={localChargers}
          pagination={false}
          rowKey="_id"
          size="small"
          className="mt-4"
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-6">
          <Empty description={t('ViewRFIDDrawer.NoChargers')} />
        </div>
      )}
    </SectionCard>
  );

  return (
    <Drawer
      maskClosable={false}
      destroyOnHidden
      footer={
        <div className="flex justify-end gap-2 items-center">
          <Button onClick={() => setOpen(false)}>
            {t('ViewRFIDDrawer.CancelButton')}
          </Button>
          {!isReadOnlyUser && (
            <Button
              type="primary"
              loading={isUpdating}
              onClick={handleUpdateRFID}
            >
              {t('ViewRFIDDrawer.UpdateButton')}
            </Button>
          )}
        </div>
      }
      open={open}
      title={
        <div className="pr-6">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {t('ViewRFIDDrawer.Title')}
          </div>
          <div className="text-base font-semibold text-slate-900 font-mono mt-0.5">
            {rfidData?.RFID ?? ''}
          </div>
        </div>
      }
      width={isMobile ? '100%' : 720}
      onClose={() => setOpen(false)}
      styles={{
        body: { background: '#f8fafc', padding: isMobile ? 12 : 20 },
        footer: { borderTop: '1px solid #e2e8f0' },
      }}
    >
      {isLoading ? (
        <div className="flex justify-center p-10">
          <Spin indicator={<LoadingOutlined spin style={{ fontSize: 24 }} />} />
        </div>
      ) : (
        <>
          {renderRFIDDetails()}
          {!isGlobal && (
            <>
              {renderLocationsSection()}
              {renderChargersSection()}
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

ViewRFIDDrawer.propTypes = {
  chargerData: PropTypes.shape({
    _id: PropTypes.string,
    RFID: PropTypes.string,
    isGlobal: PropTypes.bool,
    status: PropTypes.string,
    description: PropTypes.string,
    created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    updated_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customer: PropTypes.object,
    prePaid: PropTypes.bool,
    balance: PropTypes.number,
    limit: PropTypes.number,
    monthlyUsageSoFar: PropTypes.number,
    lifetimeUsageSoFar: PropTypes.number,
  }),
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  refetchRFIDList: PropTypes.func,
};

export default ViewRFIDDrawer;
